import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as CalendarPlanIcon } from "../../../../../../../packages/assets/report-icons/calendar-plan.svg";
import styles from "./CheckInToast.module.scss";

export const TOAST_VISIBLE_MS = 5000;
export const TOAST_FADE_MS = 150;

export interface CheckInToastProps {
  /** The sentence to show, already written for the learner. Omitted and no toast is drawn. */
  message?: string;
  /** The learner is leaving the screen it belongs to, so it fades out now. */
  leaving?: boolean;
  /** Called once the toast has faded out, however it was dismissed. */
  onDone: () => void;
}

const ToastCard: React.FC<{
  message: string;
  leaving: boolean;
  onDone: () => void;
}> = ({ message, leaving: leftScreen, onDone }) => {
  const [leaving, setLeaving] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (leftScreen) setLeaving(true);
  }, [leftScreen]);

  useEffect(() => {
    const visibleTimer = setTimeout(() => setLeaving(true), TOAST_VISIBLE_MS);
    return () => clearTimeout(visibleTimer);
  }, []);

  useEffect(() => {
    if (!leaving) return undefined;
    const exitTimer = setTimeout(() => onDoneRef.current(), TOAST_FADE_MS);
    return () => clearTimeout(exitTimer);
  }, [leaving]);

  return (
    <div
      className={styles.card}
      data-leaving={leaving}
      data-testid="report-check-in-toast"
    >
      <CalendarPlanIcon className={styles.icon} aria-hidden="true" />
      <span className={styles.message}>{message}</span>
    </div>
  );
};

/**
 * Confirms the check-in booked by the assessment the learner just submitted.
 * Leaves on its own after a few seconds.
 *
 * The live region is mounted whether or not there is a message, because
 * screen readers announce text that arrives in a region they already know,
 * not a region that arrives with its text.
 */
const CheckInToast: React.FC<CheckInToastProps> = ({
  message,
  leaving = false,
  onDone,
}) => (
  <div
    className={styles.toast}
    role="status"
    data-testid="report-check-in-region"
  >
    {message && (
      <ToastCard message={message} leaving={leaving} onDone={onDone} />
    )}
  </div>
);

export default CheckInToast;
